import { getActiveOrgId } from '@/lib/activeOrg';
import { supabase } from '@/lib/supabase';

export type PipedriveConnectionStatus = {
  isConnected: boolean;
  companyDomain: string | null;
  connectedAt: string | null;
};

export type PipedriveDealStage = {
  id: number;
  name: string;
  order_nr: number;
  pipeline_id: number;
};

export type PipedriveDealSummary = {
  totalDeals: number;
  totalValue: number;
  wonDeals: number;
  wonValue: number;
  lostDeals: number;
  lostValue: number;
  openDeals: number;
  openValue: number;
  avgDealValue: number;
  weightedValue: number;
};

// ── Pipedrive custom field keys & option maps ───────────────────────
export const PD_FIELDS = {
  CHANNEL: 'channel',
  IS_SQL: 'c4f27e7f703863ea214bd24f1122626206cc4842',
  SQL_STAGE: '9e8d88c213b2995f5b9856f01c9155cb33584ab5',
  DISQUALIFICATION_REASON: '275dac82c796b510bdb44c7fe617723260956133',
  NURTURE_REASON: 'ae739dcee4128ebc80e57785815d7c65ca94a05f',
  CAMPAIGN: '0ba124375cd5f8671765c0d05626da735fbc52d0',
} as const;

export const CHANNEL_OPTIONS: Record<number, string> = {
  195: 'Cold Email',
  196: 'Cold Call',
  224: 'Referral',
  98: 'Inbound',
  223: 'Google Ads',
  97: 'ActiveCampaign',
  106: 'Fintech Engineer',
};

export const IS_SQL_OPTIONS: Record<number, string> = {
  105: 'Fintech Qualified',
  99: 'Qualified (General)',
  100: 'Not Qualified',
  222: 'Partnership',
  101: 'Unknown',
  102: 'Spam',
  109: 'Test',
};

export const SQL_STAGE_OPTIONS: Record<number, string> = {
  216: 'New',
  217: 'Replied',
  212: 'Qualified',
  213: 'Discovery Call Booked',
  218: 'Sent Profiles',
  214: 'Incubating',
  215: 'Nurture',
  219: 'Dead',
};

export const LABEL_OPTIONS: Record<string, string> = {
  'ca0a59f8-a5d4-436b-9805-4907dfc1d7fa': 'Hot',
  '6fa07911-93ec-49db-a38d-aca4255de51c': 'Warm',
  '42ede399-893b-4607-bb5b-ba8839481d08': 'Cold',
  'e4d6bc20-3261-11f0-b007-d1a07c9d7354': 'SPAM',
  'c646bb50-5b9e-11f0-a90c-ebeba4001457': 'Not Qualified',
  'ca644090-5ba3-11f0-bdd7-dd6bcda188c1': 'Booked Call',
  '9154eca0-5cc5-11f0-9d9d-c9f32679b38b': 'Test',
  'b42f8dd0-8e3d-11f0-8e28-b7df6325a82e': 'FE',
  '03ff3ff0-350e-11f1-afc2-0fe40712001e': 'Opportunity',
  '60afbc20-350e-11f1-8734-11ba7abe6bc0': 'Partnership',
};

/** IDs considered "qualified" for SQL counting */
export const QUALIFIED_SQL_IDS = [105, 99];

export type PipedriveDeal = {
  id: number;
  title: string;
  value: number;
  currency: string;
  status: 'open' | 'won' | 'lost' | 'deleted';
  stageName: string;
  stageId: number;
  personName: string | null;
  orgName: string | null;
  addTime: string;
  updateTime: string;
  wonTime: string | null;
  lostTime: string | null;
  expectedCloseDate: string | null;
  probability: number | null;
  // Custom fields
  channelId: number | null;
  channel: string | null;
  isSQLId: number | null;
  isSQL: string | null;
  sqlStageId: number | null;
  sqlStage: string | null;
  labels: string[];
  disqualificationReason: string | null;
  nurtureReason: string | null;
  campaign: string | null;
};

export type PipedriveLead = {
  id: string;
  title: string;
  owner: string | null;
  createdAt: string;
  updatedAt: string;
  sourceOrigin: string | null;
  channelId: number | null;
  channel: string | null;
  sqlStageId: number | null;
  sqlStage: string | null;
  labels: string[];
  isArchived: boolean;
};

async function invoke(body: Record<string, any>): Promise<any> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  const { data, error } = await supabase.functions.invoke('pipedrive', {
    body: {
      ...body,
      userId: session?.user?.id,
      organizationId: getActiveOrgId(),
    },
  });

  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
}

const pipedriveService = {
  async getConnectionStatus(): Promise<PipedriveConnectionStatus> {
    const orgId = getActiveOrgId();
    let query = supabase
      .from('pipedrive_connections')
      .select('company_domain, connected_at')
      .eq('is_active', true);

    if (orgId) query = query.eq('organization_id', orgId);

    const { data } = await query.maybeSingle();

    return {
      isConnected: !!data,
      companyDomain: data?.company_domain ?? null,
      connectedAt: data?.connected_at ?? null,
    };
  },

  async connect(apiToken: string): Promise<{ companyDomain: string; userName: string }> {
    const result = await invoke({ action: 'connect', apiToken });
    return { companyDomain: result.companyDomain, userName: result.userName };
  },

  async disconnect(): Promise<void> {
    await invoke({ action: 'disconnect' });
  },

  async fetchDeals(params?: Record<string, string>): Promise<PipedriveDeal[]> {
    const result = await invoke({
      action: 'fetch',
      endpoint: 'deals',
      params: { status: 'all_not_deleted', limit: '500', ...params },
    });

    return (result.data ?? []).map((d: any) => {
      const channelId = typeof d[PD_FIELDS.CHANNEL] === 'number' ? d[PD_FIELDS.CHANNEL] : null;
      const isSQLId = typeof d[PD_FIELDS.IS_SQL] === 'number' ? d[PD_FIELDS.IS_SQL] : null;
      const sqlStageId = typeof d[PD_FIELDS.SQL_STAGE] === 'number' ? d[PD_FIELDS.SQL_STAGE] : null;
      const disqId =
        typeof d[PD_FIELDS.DISQUALIFICATION_REASON] === 'number'
          ? d[PD_FIELDS.DISQUALIFICATION_REASON]
          : null;
      const nurtureId =
        typeof d[PD_FIELDS.NURTURE_REASON] === 'number' ? d[PD_FIELDS.NURTURE_REASON] : null;
      const campaignId = typeof d[PD_FIELDS.CAMPAIGN] === 'number' ? d[PD_FIELDS.CAMPAIGN] : null;

      // Labels come as an array of UUIDs
      const rawLabels: string[] = Array.isArray(d.label) ? d.label : [];
      const labels = rawLabels.map((id: string) => LABEL_OPTIONS[id] ?? id);

      return {
        id: d.id,
        title: d.title,
        value: d.value ?? 0,
        currency: d.currency ?? 'USD',
        status: d.status,
        stageName: d.stage_id ? `Stage ${d.stage_id}` : '',
        stageId: d.stage_id,
        personName: d.person_name ?? null,
        orgName: d.org_name ?? null,
        addTime: d.add_time,
        updateTime: d.update_time,
        wonTime: d.won_time ?? null,
        lostTime: d.lost_time ?? null,
        expectedCloseDate: d.expected_close_date ?? null,
        probability: d.probability ?? null,
        channelId,
        channel: channelId !== null ? CHANNEL_OPTIONS[channelId] ?? `Channel ${channelId}` : null,
        isSQLId,
        isSQL: isSQLId !== null ? IS_SQL_OPTIONS[isSQLId] ?? `Option ${isSQLId}` : null,
        sqlStageId,
        sqlStage:
          sqlStageId !== null ? SQL_STAGE_OPTIONS[sqlStageId] ?? `Stage ${sqlStageId}` : null,
        labels,
        disqualificationReason: disqId !== null ? String(disqId) : null,
        nurtureReason: nurtureId !== null ? String(nurtureId) : null,
        campaign: campaignId !== null ? String(campaignId) : null,
      };
    });
  },

  async fetchStages(): Promise<PipedriveDealStage[]> {
    const result = await invoke({ action: 'fetch', endpoint: 'stages' });
    return (result.data ?? []).map((s: any) => ({
      id: s.id,
      name: s.name,
      order_nr: s.order_nr,
      pipeline_id: s.pipeline_id,
    }));
  },

  async fetchDealsSummary(): Promise<PipedriveDealSummary> {
    const result = await invoke({
      action: 'fetch',
      endpoint: 'deals/summary',
      params: { status: 'all_not_deleted' },
    });

    const d = result.data ?? {};
    const total = d.total_count ?? 0;
    const won = d.won_count ?? 0;
    const lost = d.lost_count ?? 0;
    const open = d.open_count ?? 0;

    const valuesTotal = d.values_total?.default ?? {};
    const wonVal = d.won_deals_formatted?.default ?? 0;
    const lostVal = d.lost_deals_formatted?.default ?? 0;

    return {
      totalDeals: total,
      totalValue: valuesTotal.value ?? 0,
      wonDeals: won,
      wonValue: typeof wonVal === 'number' ? wonVal : parseFloat(wonVal) || 0,
      lostDeals: lost,
      lostValue: typeof lostVal === 'number' ? lostVal : parseFloat(lostVal) || 0,
      openDeals: open,
      openValue: valuesTotal.value ? valuesTotal.value - (wonVal || 0) - (lostVal || 0) : 0,
      avgDealValue: total > 0 ? (valuesTotal.value ?? 0) / total : 0,
      weightedValue: d.weighted_values_total?.default?.value ?? 0,
    };
  },

  /**
   * Fetch raw lead objects for debugging field structure.
   * Call from browser console: `(await import('@/services/pipedrive')).default.debugLeads()`
   */
  async debugLeads(): Promise<any[]> {
    const result = await invoke({
      action: 'fetch',
      endpoint: 'leads',
      params: { limit: '3' },
    });
    const items = result.data ?? [];
    // eslint-disable-next-line no-console
    console.log('[Pipedrive] Raw lead objects:', items);
    // eslint-disable-next-line no-console
    if (items[0]) console.log('[Pipedrive] Lead keys:', Object.keys(items[0]));
    return items;
  },

  async fetchLeads(params?: Record<string, string>): Promise<PipedriveLead[]> {
    const allLeads: PipedriveLead[] = [];
    let start = 0;
    const limit = 500;

    // Pipedrive leads use offset pagination
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const result = await invoke({
        action: 'fetch',
        endpoint: 'leads',
        params: { limit: String(limit), start: String(start), ...params },
      });

      const items = result.data ?? [];
      for (const l of items) {
        // Channel: try multiple field locations
        // - Deals use custom field `channel` (numeric option ID)
        // - Leads may use `channel_id` (built-in), `channel`, or `source_name`
        const rawChannel = l.channel_id ?? l.channel ?? l[PD_FIELDS.CHANNEL] ?? null;
        const channelId = typeof rawChannel === 'number' ? rawChannel : null;

        // SQL Stage: try the deal custom field key, also check if it's on the lead
        const rawSqlStage = l[PD_FIELDS.SQL_STAGE] ?? null;
        const sqlStageId = typeof rawSqlStage === 'number' ? rawSqlStage : null;

        // Lead labels come as an array of UUIDs
        const rawLabels: string[] = Array.isArray(l.label_ids) ? l.label_ids : [];
        const labels = rawLabels.map((id: string) => LABEL_OPTIONS[id] ?? id);

        allLeads.push({
          id: l.id,
          title: l.title ?? '',
          owner: l.owner_id ? String(l.owner_id) : null,
          createdAt: l.add_time ?? '',
          updatedAt: l.update_time ?? '',
          sourceOrigin: l.source_name ?? null,
          channelId,
          channel: channelId !== null ? CHANNEL_OPTIONS[channelId] ?? `Channel ${channelId}` : null,
          sqlStageId,
          sqlStage:
            sqlStageId !== null ? SQL_STAGE_OPTIONS[sqlStageId] ?? `Stage ${sqlStageId}` : null,
          labels,
          isArchived: l.is_archived ?? false,
        });
      }

      // Check for more pages
      if (result.additional_data?.pagination?.more_items_in_collection && items.length === limit) {
        start += limit;
      } else {
        break;
      }
    }

    return allLeads;
  },

  async fetchActivities(params?: Record<string, string>): Promise<any[]> {
    const result = await invoke({
      action: 'fetch',
      endpoint: 'activities',
      params: { limit: '100', ...params },
    });
    return result.data ?? [];
  },
};

export default pipedriveService;
